// src/services/receipts.js — delivery & read receipts from the web device.
//
// ✔️ RECEIPTS ARE THE CHAT'S CURSORS, from every device the same way. The phone reports over the
//    signaling socket: `report-delivered` / `report-seen` per message (the server pushes the tick to
//    the sender at once, keeps the millis watermarks, arms a disappearing message), and `ack` with
//    the highest seq it has received / read — which moves `chats/{id}.receiptsSeq`, the ONE thing
//    the ticks are drawn from (app: src/utils/seqReceipts; here: ui/MessageTicks).
//    The web joins the same socket (services/socket) and now reports the same way.
//
// 🟥 IT USED TO WRITE THE RECEIPT ONTO EVERY MESSAGE DOCUMENT (delivered / seen / deliveredTo /
//    seenBy). Nothing reads those any more — and every such write sent the whole document, with a
//    sealed copy of its text for every device, back down to every phone listening to the chat.
//    And it never moved the seq cursors at all: a message read on the web never turned blue on the
//    sender's phone.
//
// The chat-doc millis cursors (deliveredUpTo / readUpTo) are still advanced from here too — older
// phones and the badge logic read them — and HTTP /api/report-delivered is the fallback when no
// socket is up.

import {
  doc, collection, setDoc, runTransaction, onSnapshot,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { getSocket } from './socket';
import { cachedWebDeviceId, warmWebDeviceId } from './sendDoc';
/* 🟢 THE ONE CONSTANT, not a second copy of the string. A uid written out by
   hand in a second file is a uid that is wrong the day it changes. */
import { isOfficialUid } from './support';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'https://signaling-server-prod-397203671414.us-central1.run.app';

// local forward-only guards so we never spam writes or move a cursor backwards
const _reported = new Set();               // `${chatId}|${msgId}|${kind}`
const _cursor = new Map();                 // `${chatId}|${kind}` -> millis

function msgMillis(m) {
  const t = m?.timestamp;
  if (!t) return Date.now();
  if (typeof t === 'number') return t;
  if (t.seconds) return t.seconds * 1000;
  if (typeof t.toDate === 'function') { try { return t.toDate().getTime(); } catch { return Date.now(); } }
  return new Date(t).getTime() || Date.now();
}

/* ══ ONE WRITER PER CHAT DOCUMENT ══════════════════════════════════════════
   🟥 THE CONTENTION WAS OURS. `advanceCursor` was called once per message per
      kind — a screen with eight unread messages fires eight `delivered` and
      eight `seen` advances within a few milliseconds, and every one of them
      opened its OWN transaction against the SAME chat document. A transaction
      asserts the document is unchanged at commit, so they knocked each other
      over: a burst of `failed-precondition`, and the watermark — the field the
      other person's green tick is derived from — stopped moving. The retry
      loop below could not help, because the thing we were losing the race to
      was ourselves, retrying in lockstep.

      The forward-only guard did not prevent it either: `_cursor` is set only
      AFTER a successful commit (deliberately — see the note there), so all
      eight callers read the same stale value and all eight passed it.

   🟢 SERIALIZE PER DOCUMENT, NOT PER FIELD. The lock has to match what
      Firestore actually contends on, which is the chat DOCUMENT — serializing
      per chat+kind would still have `delivered` and `seen` fighting each other
      over the same doc, which is exactly the pattern in the crash report
      (four writes, two fields, one document, 1.2 seconds).

   🟢 AND COALESCE WHILE SERIALIZED. A call arriving while a write is in flight
      queues no write of its own: it raises `_want`, the highest timestamp
      anyone has asked for. The running writer re-reads `_want` when it
      finishes and only goes again if it moved. Eight messages therefore
      produce ONE transaction carrying the newest timestamp — which is what a
      watermark means anyway — and both fields ride in it together. */
const _want = new Map();       // `${chatId}|${kind}` -> highest millis requested
const _chain = new Map();      // chatId -> promise of the writer currently running

const KINDS = ['delivered', 'seen'];
const FIELD_OF = { delivered: 'deliveredUpTo', seen: 'readUpTo' };

/* 🟢 A BEAT BEFORE THE FIRST WRITE. The callers arrive in a burst — one per
      message, synchronously, from the same effect — so pausing briefly lets
      the whole batch land in `_want` and collapse into a single transaction.
      Without it the first message writes immediately and the other seven
      collapse into a second write: correct, but twice the traffic on the
      document everyone else is also writing. */
const SETTLE_MS = 90;

function advanceCursor(chatId, uid, kind, millis) {
  if (!chatId || !uid || !(millis > 0)) return Promise.resolve();
  const key = `${chatId}|${kind}`;
  if ((_cursor.get(key) || 0) >= millis) return Promise.resolve();  // already past it

  _want.set(key, Math.max(_want.get(key) || 0, millis));

  // a writer is already on this document — it will pick up the raised target
  const running = _chain.get(chatId);
  if (running) return running;

  const run = (async () => {
    try {
      await new Promise((r) => setTimeout(r, SETTLE_MS));
      /* drain: keep writing until nobody has asked for anything newer.
         Normally one pass; a second only when messages arrived mid-write. */
      for (;;) {
        const targets = {};
        for (const k of KINDS) {
          const t = _want.get(`${chatId}|${k}`) || 0;
          if (t > (_cursor.get(`${chatId}|${k}`) || 0)) targets[k] = t;
        }
        if (!Object.keys(targets).length) break;
        // eslint-disable-next-line no-await-in-loop
        const ok = await writeCursors(chatId, uid, targets);
        if (!ok) break;                 // it failed; don't spin on it
      }
    } finally {
      /* nothing awaits between the loop's last check and this line, so a call
         arriving now cannot be dropped — it either raised `_want` before the
         check (and was written) or finds no writer here and starts one. */
      _chain.delete(chatId);
    }
  })();

  _chain.set(chatId, run);
  return run;
}

/**
 * Move one or both cursors in a single transaction.
 * @param targets `{ delivered?: ms, seen?: ms }`
 * @returns true when it landed
 */
async function writeCursors(chatId, uid, targets) {
  const fields = Object.keys(targets).map((k) => FIELD_OF[k]).join(' + ');
  try {
    // 🟥 FORWARD-ONLY AGAINST THE SERVER, NOT JUST AGAINST THIS TAB. The in-memory guard
    //    starts empty on every page load, so a plain merge could push the watermark
    //    BACKWARDS — read a chat on your phone, then open the web and scroll to an older
    //    message, and this would rewind readUpTo to that message's time. The phone's own
    //    cursor is forward-only so it shrugs, but a reinstalled phone SEEDS from this
    //    field, and would then resurrect every message after the rewind as unread.
    //    The phone guards its own mirror the same way (ChatModal.mirrorReadUpTo).
    /* 🟥 RETRY, WITH SPACE BETWEEN ATTEMPTS. This transaction reads the chat
       document and asserts it is unchanged at commit — so on a busy chat it loses
       that race and throws `failed-precondition`. Firestore retries internally,
       but back-to-back and only a few times, which is no help when the document
       is being written CONTINUOUSLY: every send touches lastMessage, and the
       other participant is advancing their own cursors on the same doc.

       When it gives up, `readUpTo` silently stops moving — and `readUpTo` is what
       the other person's green tick is derived from, so the visible symptom is
       "their ticks never turn green" with no error anywhere near the ticks.

       Spaced, jittered attempts are the fix for contention we do NOT control;
       jitter matters because both sides retry, and a fixed delay would have
       them collide again in lockstep. (Contention we DO control is handled by
       the serializer above — retries never fixed that and never could.) */
    const attempt = async () => runTransaction(db, async (tx) => {
      const ref = doc(db, 'chats', chatId);
      const snap = await tx.get(ref);
      if (!snap.exists()) return;
      const data = snap.data() || {};
      const patch = {};
      for (const [kind, millis] of Object.entries(targets)) {
        const f = FIELD_OF[kind];
        const cur = data?.[f]?.[uid];
        const curMs = typeof cur === 'number' ? cur : (cur?.toMillis?.() || (cur?.seconds ? cur.seconds * 1000 : 0));
        // someone already moved it past us — nothing to do, and NOT a failure
        if (millis > curMs) patch[f] = { [uid]: millis };
      }
      if (!Object.keys(patch).length) return;
      tx.set(ref, patch, { merge: true });
    });

    let lastErr = null;
    for (let i = 0; i < 4; i++) {
      try { await attempt(); lastErr = null; break; } catch (e) {
        lastErr = e;
        if (i === 3) break;
        const wait = [120, 350, 900][i] + Math.floor(Math.random() * 150);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, wait));
      }
    }
    if (lastErr) throw lastErr;
    // 🟢 mark it spent only once it actually landed — setting this before the write meant
    //    a failed advance poisoned the pair for the rest of the session, so a retry at
    //    the same or a lower millis was silently dropped and the watermark never moved
    for (const [kind, millis] of Object.entries(targets)) {
      _cursor.set(`${chatId}|${kind}`, millis);
    }
    return true;
  } catch (e) {
    // 🟥 NO PLAIN-MERGE FALLBACK HERE. The transaction exists precisely because this
    //    guard is empty on every page load; a merge in the catch throws that away in the
    //    one case it was written for — contention, i.e. the phone advancing the same
    //    cursor concurrently — and would write the watermark BACKWARDS. A reinstalled
    //    phone seeds its cursor from this field, so a rewind resurrects every message
    //    after it as unread. Losing one advance is recoverable; rewinding isn't.
    //
    // 🟢 BUT IT IS NO LONGER SILENT. `readUpTo` is the sender's read RECEIPT as much
    //    as it is my watermark, so an advance that never lands shows up on the other
    //    person's screen as ticks that stay grey forever — with nothing anywhere
    //    saying why. `failed-precondition` here means the chat document changed
    //    between this transaction's read and its commit (Firestore retries a few
    //    times on its own); seeing it repeatedly means something is writing that
    //    document constantly.
    console.warn(
      `[receipts] ${fields} advance did not land for ${chatId}:`,
      e && (e.code || e.message),
    );
    return false;
  }
}

/**
 * Is this a message from the official Allo account?
 *
 * 🟥 THERE IS NOTHING FOR A RECEIPT TO TELL `allo_official`. It is not a person
 *    with a phone waiting to see two blue ticks; it is a server that already
 *    wrote `delivered: true` on the message before anybody read it. Reporting
 *    against it is a write with no reader.
 *
 * 🟥 AND THE WRITE IS REFUSED. The deployed rule allows an update only when the
 *    document's `senderId` is the caller, so an `updateDoc` on a message Allo
 *    sent is denied — silently, because the call site swallows the error. The
 *    SDK still applies it locally first and rolls it back when the server says
 *    no, and that round trip is a visible flicker on the bubble.
 */
const isFromAllo = (m) => isOfficialUid(m?.senderId);

/* ── the seq cursor, coalesced per chat ───────────────────────────────────────
   A screen of eight unread messages reports eight times in a few milliseconds; the cursor is a
   high-water mark, so ONE `ack` carrying the highest seq of each kind says all of it. */
const ACK_SETTLE_MS = 150;
const _ackWant = new Map();    // chatId -> { delivered: seq, read: seq, uid }
const _ackTimer = new Map();   // chatId -> timer
function _ack(chatId, uid, kind, seq) {
  const s = Number(seq) || 0;
  if (!chatId || !uid || !(s > 0)) return;
  const w = _ackWant.get(chatId) || { delivered: 0, read: 0, uid };
  w[kind] = Math.max(w[kind] || 0, s);
  w.uid = uid;
  _ackWant.set(chatId, w);
  if (_ackTimer.has(chatId)) return;
  _ackTimer.set(chatId, setTimeout(async () => {
    _ackTimer.delete(chatId);
    const want = _ackWant.get(chatId);
    _ackWant.delete(chatId);
    /* this browser's own device id (the cursor is kept per device) — known before any send */
    let dev = cachedWebDeviceId();
    if (!dev) { try { dev = await warmWebDeviceId(); } catch { dev = null; } }
    const sock = getSocket();
    if (!want || !sock) return;
    try {
      sock.emit('ack', {
        chatId,
        deviceId: dev || `web_${want.uid}`,
        userId: want.uid,
        ...(want.delivered > 0 ? { ackedSeq: want.delivered } : {}),
        ...(want.read > 0 ? { readSeq: want.read } : {}),
        seqReceipts: true,
      });
    } catch {}
  }, ACK_SETTLE_MS));
}
const _seqOf = (m) => (Number(m?.seq) > 0 ? Number(m.seq) : undefined);

/** Mark ONE incoming message delivered (call as soon as it reaches the web). */
export async function reportDelivered(chatId, m, myUid) {
  if (!chatId || !m?.id || m.senderId === myUid || m.delivered) return;
  if (isFromAllo(m)) return;
  const key = `${chatId}|${m.id}|delivered`;
  if (_reported.has(key)) return;
  _reported.add(key);
  const ms = msgMillis(m);
  const payload = { chatId, messageId: m.id, senderId: m.senderId, recipientId: myUid, type: m.type || 'text', messageSeq: _seqOf(m), messageMillis: ms };
  const sock = getSocket();
  if (sock) {
    try { sock.emit('report-delivered', payload); } catch {}
  } else {
    /* no socket up: the HTTP twin of the same report (it moves the same cursor) */
    try {
      fetch(`${BACKEND}/api/report-delivered`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => {});
    } catch {}
  }
  _ack(chatId, myUid, 'delivered', m.seq);
  advanceCursor(chatId, myUid, 'delivered', ms);
}

/** Mark ONE incoming message read (call when it's actually on screen). */
export async function reportSeen(chatId, m, myUid) {
  if (!chatId || !m?.id || m.senderId === myUid || m.seen) return;
  /* 🟥 a support message from the official account has nobody to tell — see `isFromAllo` */
  if (isFromAllo(m)) return;
  const key = `${chatId}|${m.id}|seen`;
  if (_reported.has(key)) return;
  _reported.add(key);
  const ms = msgMillis(m);
  const sock = getSocket();
  if (sock) {
    try { sock.emit('report-seen', { chatId, messageId: m.id, senderId: m.senderId, recipientId: myUid, type: m.type || 'text', messageSeq: _seqOf(m), messageMillis: ms }); } catch {}
  }
  _ack(chatId, myUid, 'read', m.seq);
  advanceCursor(chatId, myUid, 'delivered', ms);
  advanceCursor(chatId, myUid, 'seen', ms);
}

/** Convenience: report the whole visible batch. */
export async function reportBatch(chatId, msgs, myUid, { seen }) {
  for (const m of msgs) {
    if (m.senderId === myUid) continue;
    if (seen) await reportSeen(chatId, m, myUid);
    else await reportDelivered(chatId, m, myUid);
  }
}

/**
 * "I opened this chat." Clears the badge on BOTH clients.
 *
 * 🟥 Three writes, and each one is load-bearing for a different reader:
 *   • readUpTo[me]        — the only thing the PHONE honours. Its chats snapshot feeds
 *                           this into advanceReadCursor(), which is what actually makes
 *                           its badge (and its unread divider) go away.
 *   • unreadCounts[me]=0  — what THIS client's badge reads, and what older app builds read.
 *   • manuallyUnread[me]  — the phone's "mark as unread" swipe. Opening the chat has to
 *                           undo it, or the green dot survives being read.
 *
 * `newestMs` must be the newest message's OWN timestamp, never Date.now(): the server
 * compares watermarks across participants to decide when a sender goes green, and a
 * now-floor from one device would falsely satisfy that for everyone.
 */
export async function markChatRead(chatId, myUid, newestMs, { publishRead = true } = {}) {
  if (!chatId || !myUid) return;
  // 🟥 NOTHING TO READ MEANS NOTHING TO MARK. Called with newestMs === 0 (the chat's
  //    messages haven't decrypted yet) this used to still zero unreadCounts — clearing
  //    the badge HERE while writing no watermark, so the phone kept its own badge and
  //    the two clients disagreed. It also silently undid a "mark as unread" swipe on a
  //    chat you merely clicked through.
  if (!(newestMs > 0)) return;

  /* 🔒 READ RECEIPTS OFF → the read watermark is the one thing we must not publish:
     `readUpTo[me]` is precisely what the sender's green tick is derived from. The
     two writes below still run — they are mine and tell the sender nothing. */
  noteChatRead(chatId, myUid, newestMs).catch(() => {});
  if (publishRead) await advanceCursor(chatId, myUid, 'seen', newestMs);
  try {
    await setDoc(
      doc(db, 'chats', chatId),
      { unreadCounts: { [myUid]: 0 }, manuallyUnread: { [myUid]: false } },
      { merge: true },
    );
  } catch {}
}

/* ══ MY OWN READ POSITION ══════════════════════════════════════════════════
   🟥 WHY THIS EXISTS SEPARATELY FROM `readUpTo`. That field is a RECEIPT — it is
      what the other person's green tick is computed from — so it is not written
      at all when read receipts are off. With receipts off, nothing recorded that
      I had read a chat, and no other device of mine could learn it: read on the
      web, and the phone kept its badge forever.

      `users/{me}/chatReads/{chatId}.at` is the private half. Written on every
      read regardless of the setting, readable only by me (see firestore.rules),
      and it tells the other side nothing. The badge logic vetoes on the MAX of
      the two — see unreadFor() in services/chats.js. */

/** Record that I have read this chat up to `ms`. Forward-only, like the cursors. */
export async function noteChatRead(chatId, myUid, ms) {
  if (!chatId || !myUid || !(ms > 0)) return;
  const key = `${chatId}|mine`;
  if ((_cursor.get(key) || 0) >= ms) return;
  _cursor.set(key, ms);
  try {
    await setDoc(doc(db, 'users', myUid, 'chatReads', chatId), { at: ms }, { merge: true });
  } catch (e) {
    _cursor.delete(key);         // let a retry through rather than poisoning the pair
    console.warn('[receipts] could not record my own read position:', e && (e.code || e.message));
  }
}

/**
 * Every chat's private read mark, live: `{ [chatId]: ms }`.
 *
 * 🟢 ONE LISTENER ON THE COLLECTION, not one per chat — this feeds the whole
 *    sidebar's badge calculation, and a listener per row would be dozens.
 */
export function watchMyReads(myUid, cb) {
  if (!myUid) { cb({}); return () => {}; }
  return onSnapshot(
    collection(db, 'users', myUid, 'chatReads'),
    (snap) => {
      const out = {};
      snap.docs.forEach((d) => {
        const v = d.data()?.at;
        const ms = typeof v === 'number' ? v
          : (v?.toMillis?.() ?? (v?.seconds ? v.seconds * 1000 : 0));
        if (ms) out[d.id] = ms;
      });
      cb(out);
    },
    (e) => {
      console.warn('[receipts] chatReads listener failed:', e && (e.code || e.message));
      cb({});
    },
  );
}
